export const TABLE_CART_EVENT_KIND = Object.freeze({
  POSITIVE: "positive",
  DESTRUCTIVE: "destructive",
  UNKNOWN: "unknown",
});

const POSITIVE_REASONS = new Set([
  "order-created",
  "order-created-legacy",
  "items-added",
  "item-status-updated",
]);

const DESTRUCTIVE_REASONS = new Set([
  "item-cancelled",
  "item-quantity-updated",
]);

export const classifyTableCartChangeReason = (reason) => {
  const normalized = String(reason || "").trim().toLowerCase();
  if (POSITIVE_REASONS.has(normalized)) return TABLE_CART_EVENT_KIND.POSITIVE;
  if (DESTRUCTIVE_REASONS.has(normalized)) {
    return TABLE_CART_EVENT_KIND.DESTRUCTIVE;
  }
  return TABLE_CART_EVENT_KIND.UNKNOWN;
};

export const resolveTableCartEventPolicy = (reason) => {
  const kind = classifyTableCartChangeReason(reason);
  return {
    kind,
    shouldMarkOccupied: kind === TABLE_CART_EVENT_KIND.POSITIVE,
    shouldReconcile: kind === TABLE_CART_EVENT_KIND.DESTRUCTIVE,
  };
};

const normalizeId = (value) => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const normalizeEvidence = (evidence = {}) => ({
  orderIds: [
    ...(Array.isArray(evidence.orderIds) ? evidence.orderIds : []),
    evidence.orderId,
  ]
    .map(normalizeId)
    .filter(Boolean),
  orderItemIds: [
    ...(Array.isArray(evidence.orderItemIds) ? evidence.orderItemIds : []),
    evidence.orderItemId,
  ]
    .map(normalizeId)
    .filter(Boolean),
  sources: [
    ...(Array.isArray(evidence.sources) ? evidence.sources : []),
    evidence.source,
  ]
    .map((value) => String(value || "").trim())
    .filter(Boolean),
});

const unique = (values) => Array.from(new Set(values));

const evidenceMatchesMutation = (evidence, mutation = {}) => {
  if (!evidence) return false;
  const orderId = normalizeId(mutation.orderId);
  const orderItemId = normalizeId(mutation.orderItemId);
  if (orderItemId && evidence.orderItemIds.includes(orderItemId)) return true;
  if (orderId && evidence.orderIds.includes(orderId)) return true;
  return false;
};

export const createDestructiveCartEvidenceRegistry = ({
  ttlMs = 15000,
  now = () => Date.now(),
  schedule = (callback, delay) => setTimeout(callback, delay),
  cancel = (timer) => clearTimeout(timer),
} = {}) => {
  const entries = new Map();
  const tableKey = (tableId) => String(tableId ?? "");

  const invalidate = (tableId) => {
    const key = tableKey(tableId);
    const existing = entries.get(key);
    if (existing?.timer) cancel(existing.timer);
    entries.delete(key);
  };

  const record = (tableId, evidence, generation) => {
    const key = tableKey(tableId);
    if (!key) return null;
    const normalized = normalizeEvidence(evidence);
    if (!normalized.orderIds.length && !normalized.orderItemIds.length) {
      return null;
    }
    let existing = entries.get(key);
    if (existing && (existing.consumed || now() >= existing.expiresAt)) {
      invalidate(tableId);
      existing = null;
    }
    if (existing?.timer) cancel(existing.timer);
    const createdAt = existing?.createdAt ?? now();
    const expiresAt = existing?.expiresAt ?? createdAt + ttlMs;
    const next = {
      tableId: key,
      generation: Number(generation) || 0,
      orderIds: unique([...(existing?.orderIds || []), ...normalized.orderIds]),
      orderItemIds: unique([
        ...(existing?.orderItemIds || []),
        ...normalized.orderItemIds,
      ]),
      sources: unique([...(existing?.sources || []), ...normalized.sources]),
      createdAt,
      expiresAt,
      consumed: false,
      timer: null,
    };
    next.timer = schedule(() => {
      if (entries.get(key) === next) entries.delete(key);
    }, Math.max(0, expiresAt - now()));
    entries.set(key, next);
    return { ...next, timer: undefined };
  };

  const peek = (tableId) => {
    const key = tableKey(tableId);
    const entry = entries.get(key);
    if (!entry) return null;
    if (entry.consumed || now() >= entry.expiresAt) {
      invalidate(tableId);
      return null;
    }
    return entry;
  };

  const getValid = (tableId, generation) => {
    const entry = peek(tableId);
    if (!entry) return null;
    if (Number(entry.generation) !== Number(generation)) {
      invalidate(tableId);
      return null;
    }
    return entry;
  };

  const rebaseMatching = (tableId, mutation, generation, extraEvidence = {}) => {
    const existing = peek(tableId);
    if (!evidenceMatchesMutation(existing, mutation)) return null;
    return record(
      tableId,
      {
        ...extraEvidence,
        orderId: mutation.orderId,
        orderItemId: mutation.orderItemId,
      },
      generation,
    );
  };

  const consume = (tableId, generation) => {
    const entry = getValid(tableId, generation);
    if (!entry) return null;
    const consumed = { ...entry, consumed: true, timer: undefined };
    invalidate(tableId);
    return consumed;
  };

  const clear = () => {
    Array.from(entries.keys()).forEach(invalidate);
  };

  return {
    clear,
    consume,
    getValid,
    invalidate,
    peek,
    rebaseMatching,
    record,
  };
};
